import { c as _c } from "react/compiler-runtime";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { companyMembersSelector, fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { fetchCompanyMembers } from "@store/members/members-slice-thunk";
import { fetchingProjectsFlagsSelector, projectMembersSelector } from "@store/projects/projects-selector";
import { useCompanyId } from "@context-providers/company-id-provider";
import { canUseTeamsSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchTeams } from "@store/teams/teams-slice-thunk";
import { fetchingUserFlagsSelector } from "@store/user/user-selector";
import { useEffect, useMemo } from "react";
/**
 * Custom hook that returns the company and project members and their fetching status.
 * For company members, only if current user has permission to view the company members list the members are returned.
 * If the user does not have permission then it returns an empty array.
 */
export function useMembers() {
  const $ = _c(16);
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    isFetchingCurrentUser
  } = useAppSelector(fetchingUserFlagsSelector);
  const companyMembers = useAppSelector(companyMembersSelector);
  const {
    isFetchingCompanyMembers
  } = useAppSelector(fetchingMembersFlagsSelector);
  const projectMembers = useAppSelector(projectMembersSelector);
  const {
    isFetchingProjects,
    isFetchingProjectContext
  } = useAppSelector(fetchingProjectsFlagsSelector);
  const {
    canFetchTeams
  } = useHasUserValidRoleCompanyLevel();
  const canUseTeams = useAppSelector(canUseTeamsSelector);
  const {
    canViewCompanyMembers
  } = useHasUserValidFeatureCompanyLevel();
  const {
    canViewAllProjectMembers
  } = useHasUserValidPermissionProjectLevel();
  let t0;
  bb0: {
    if (isFetchingCurrentUser || isFetchingProjects || isFetchingProjectContext) {
      t0 = false;
      break bb0;
    }
    t0 = canViewAllProjectMembers || canViewCompanyMembers;
  }
  const hasPermissionToViewMembers = t0;
  let t1;
  if ($[0] !== companyMembers || $[1] !== hasPermissionToViewMembers) {
    t1 = hasPermissionToViewMembers ? companyMembers : [];
    $[0] = companyMembers;
    $[1] = hasPermissionToViewMembers;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const members = t1;
  let t2;
  let t3;
  if ($[3] !== canFetchTeams || $[4] !== canUseTeams || $[5] !== companyId || $[6] !== dispatch || $[7] !== hasPermissionToViewMembers) {
    t2 = () => {
      if (hasPermissionToViewMembers) {
        dispatch(fetchCompanyMembers({
          companyId
        }));
        if (canFetchTeams && canUseTeams) {
          dispatch(fetchTeams({}));
        }
      }
    };
    t3 = [companyId, dispatch, hasPermissionToViewMembers, canFetchTeams, canUseTeams];
    $[3] = canFetchTeams;
    $[4] = canUseTeams;
    $[5] = companyId;
    $[6] = dispatch;
    $[7] = hasPermissionToViewMembers;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== hasPermissionToViewMembers || $[11] !== isFetchingCompanyMembers || $[12] !== isFetchingProjects || $[13] !== members || $[14] !== projectMembers) {
    t4 = {
      companyMembers: members,
      isFetchingCompanyMembers,
      projectMembers,
      isFetchingProjectMembers: isFetchingProjects,
      hasPermissionToViewMembers
    };
    $[10] = hasPermissionToViewMembers;
    $[11] = isFetchingCompanyMembers;
    $[12] = isFetchingProjects;
    $[13] = members;
    $[14] = projectMembers;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}